// src/components/shared/modals/BaseModal.tsx
// Shared modal shell: overlay, container, header with close button.
// Content and footer are passed as children.
// UI Phase 2.6: BaseModal is now a thin convenience layer over ui/ModalShell —
// the single Radix core (focus trap/restore, scroll lock, Escape, backdrop, and
// the isLoading dismiss guard all live there). BaseModal only adds the standard
// header (icon + title + subtitle + close button). Prop API is unchanged.
import React, { useId } from 'react';
import ModalShell from '../../ui/ModalShell';
import { X, type LucideIcon } from 'lucide-react';

export interface ModalIcon {
  Component: LucideIcon;
  bgColor: string;
  color: string;
}

interface BaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  icon?: ModalIcon | null;
  maxWidth?: string;
  isLoading?: boolean;
  children: React.ReactNode;
  className?: string;
}

const BaseModal = ({
  isOpen,
  onClose,
  title,
  subtitle,
  icon,
  maxWidth = 'max-w-md',
  isLoading = false,
  children,
  className = '',
}: BaseModalProps) => {
  const subtitleId = useId();

  return (
    <ModalShell
      open={isOpen}
      onClose={onClose}
      ariaLabel={title}
      maxWidth={maxWidth}
      isLoading={isLoading}
      className={className}
      describedBy={subtitle ? subtitleId : undefined}
    >
      {/* Header */}
      <div className="px-6 py-4 border-b border-line-soft flex items-center justify-between">
        <div className="flex items-center gap-3">
          {icon && (
            <div className={`p-2 ${icon.bgColor} rounded-full`}>
              <icon.Component className={`h-5 w-5 ${icon.color}`} />
            </div>
          )}
          <div>
            <h2 className="text-lg font-semibold text-ink">{title}</h2>
            {subtitle && (
              <p id={subtitleId} className="text-sm text-ink-2 mt-1">
                {subtitle}
              </p>
            )}
          </div>
        </div>
        <button
          onClick={onClose}
          aria-label="Close dialog"
          className="p-2 rounded-control text-ink-3 hover:text-ink hover:bg-surface-sunken transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          disabled={isLoading}
        >
          <X className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>

      {/* Content + Footer passed as children */}
      {children}
    </ModalShell>
  );
};

export default BaseModal;
